import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';
import { fmtDate } from './lib/fmtDate';

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';
const fmt  = (n) => n ? `$${Number(n).toFixed(0)}` : '$0';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' }) : '—';

export default function RentalContractAdmin({ orders = [] }) {
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all'); // all | signed | unsigned
  const [sending,   setSending]   = useState(null);  // order id currently sending reminder
  const [sentMap,   setSentMap]   = useState({});     // { [id]: true } after sent
  const [expanded,  setExpanded]  = useState(null);  // expanded row id

  const filtered = orders.filter(o => {
    if (filter === 'signed'   && !o.signed_at) return false;
    if (filter === 'unsigned' &&  o.signed_at) return false;
    if (search) {
      const q = search.toLowerCase();
      return (o.client_name||'').toLowerCase().includes(q) ||
             (o.signer_name||'').toLowerCase().includes(q) ||
             (o.client_email||'').toLowerCase().includes(q) ||
             (o.signer_email||'').toLowerCase().includes(q) ||
             String(o.id).includes(q);
    }
    return true;
  });

  const signed   = orders.filter(o => o.signed_at);
  const unsigned = orders.filter(o => !o.signed_at);
  const totalVal = signed.reduce((s,o) => s + Number(o.total||0), 0);

  const sendReminder = async (order) => {
    setSending(order.id);
    try {
      await axios.post(`/api/rentals/contracts/${order.id}/remind`);
      setSentMap(m => ({ ...m, [order.id]: true }));
      setTimeout(() => setSentMap(m => { const n={...m}; delete n[order.id]; return n; }), 4000);
    } catch(e) {
      alert('Could not send reminder: ' + (e.response?.data?.error || e.message));
    }
    setSending(null);
  };

  const S = {
    wrap:  { fontFamily:"'DM Sans',Arial,sans-serif" },
    stats: { display:'flex', gap:12, padding:'16px 16px 0', flexWrap:'wrap' },
    stat:  { background:'#1e2235', border:'1px solid #2e3150', borderRadius:10, padding:'12px 18px', minWidth:100 },
    sVal:  { fontSize:22, fontWeight:800, color:'#e2e8f0', lineHeight:1 },
    sLbl:  { fontSize:11, color:'#4a5568', marginTop:4 },
    toolbar: { display:'flex', gap:10, padding:'14px 16px', alignItems:'center', flexWrap:'wrap' },
    input: { background:'#252838', border:'1px solid #2e3150', borderRadius:8, padding:'8px 12px', color:'#e2e8f0', fontSize:13, outline:'none', width:220 },
    pill:  (active) => ({ padding:'6px 14px', borderRadius:20, border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
      background: active ? '#f97316' : '#2e3150', color: active ? '#fff' : '#64748b' }),
    table: { width:'100%', borderCollapse:'collapse' },
    th:    { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#4a5568', fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px', borderBottom:'1px solid #2e3150', whiteSpace:'nowrap' },
    td:    { padding:'11px 14px', fontSize:13, borderBottom:'1px solid #252838', verticalAlign:'middle' },
    badge: (signed) => ({ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:600,
      background: signed ? '#14532d' : '#2e3150', color: signed ? '#4ade80' : '#64748b' }),
    btn:   (v) => ({ padding: v==='sm'?'5px 12px':'8px 16px', borderRadius:6, border:'none', fontWeight:600,
      fontSize:12, cursor:'pointer', background: v==='blue'?'#1e3a5f': v==='red'?'#3b0a0a': v==='ghost'?'transparent':'#2e3150',
      color: v==='blue'?'#60a5fa': v==='red'?'#f87171': v==='ghost'?'#64748b':'#94a3b8' }),
    expand: { background:'#181928', borderTop:'none' },
    detail: { padding:'16px 20px', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 },
    dItem: { background:'#252838', borderRadius:8, padding:'10px 14px' },
    dLbl:  { fontSize:10, color:'#4a5568', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:4 },
    dVal:  { fontSize:13, color:'#e2e8f0', fontWeight:600 },
  };

  return (
    <div style={S.wrap}>
      {/* Stats */}
      <div style={S.stats}>
        <div style={S.stat}>
          <div style={S.sVal}>{orders.length}</div>
          <div style={S.sLbl}>Total Orders</div>
        </div>
        <div style={S.stat}>
          <div style={{...S.sVal, color:'#4ade80'}}>{signed.length}</div>
          <div style={S.sLbl}>Signed</div>
        </div>
        <div style={{...S.stat, border:'1px solid #2a1a1a'}}>
          <div style={{...S.sVal, color:'#f87171'}}>{unsigned.length}</div>
          <div style={S.sLbl}>Awaiting Signature</div>
        </div>
        <div style={S.stat}>
          <div style={{...S.sVal, color:'#f97316'}}>{fmt(totalVal)}</div>
          <div style={S.sLbl}>Signed Value</div>
        </div>
        <div style={S.stat}>
          <div style={{...S.sVal, color:'#60a5fa'}}>
            {orders.length ? Math.round((signed.length/orders.length)*100) : 0}%
          </div>
          <div style={S.sLbl}>Signature Rate</div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <input
          style={S.input}
          placeholder="🔍 Search client, email, order #..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
        />
        <div style={{display:'flex', gap:6}}>
          {[['all','All'],['signed','Signed'],['unsigned','Awaiting']].map(([v,l]) => (
            <button key={v} style={S.pill(filter===v)} onClick={()=>setFilter(v)}>{l}</button>
          ))}
        </div>
        <div style={{marginLeft:'auto', fontSize:12, color:'#4a5568'}}>{filtered.length} contracts</div>
      </div>

      {/* Table */}
      <div style={{overflowX:'auto'}}>
        <table style={S.table}>
          <thead>
            <tr style={{background:'#1e2235'}}>
              <th style={S.th}>#</th>
              <th style={S.th}>Client</th>
              <th style={S.th}>Rental Period</th>
              <th style={S.th}>Total</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Signed At</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{...S.td, textAlign:'center', color:'#3a3f60', padding:40}}>
                {search || filter !== 'all' ? 'No contracts match your filter' : 'No contracts yet'}
              </td></tr>
            )}
            {filtered.map(o => (
              <React.Fragment key={o.id}>
                <tr
                  style={{cursor:'pointer', background: expanded===o.id ? '#252838' : 'transparent'}}
                  onMouseEnter={e => { if (expanded!==o.id) e.currentTarget.style.background='#2a2d40'; }}
                  onMouseLeave={e => { if (expanded!==o.id) e.currentTarget.style.background='transparent'; }}
                  onClick={() => setExpanded(expanded===o.id ? null : o.id)}
                >
                  <td style={S.td}><span style={{fontSize:11, color:'#4a5568'}}>#{o.id}</span></td>
                  <td style={S.td}>
                    <div style={{fontSize:13, fontWeight:600, color:'#e2e8f0'}}>{o.client_name || o.signer_name || '—'}</div>
                    <div style={{fontSize:11, color:'#4a5568'}}>{o.client_email || o.signer_email || ''}</div>
                  </td>
                  <td style={S.td}>
                    <span style={{fontSize:11, color:'#94a3b8'}}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
                  </td>
                  <td style={S.td}><span style={{fontWeight:600}}>{fmt(o.total)}</span></td>
                  <td style={S.td}>
                    <span style={S.badge(!!o.signed_at)}>{o.signed_at ? '✓ Signed' : 'Awaiting'}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{fontSize:11, color:'#64748b'}}>{fmtDateTime(o.signed_at)}</span>
                  </td>
                  <td style={S.td} onClick={e => e.stopPropagation()}>
                    <div style={{display:'flex', gap:6, alignItems:'center'}}>
                      {o.signed_at && (
                        <button
                          style={{...S.btn('blue'), textDecoration:'none'}}
                          onClick={async () => {
                            try {
                              const res = await axios.get(`/api/rentals/contracts/${o.id}/pdf`, { responseType:'blob' });
                              const url = URL.createObjectURL(new Blob([res.data], { type:'application/pdf' }));
                              window.open(url, '_blank');
                            } catch(e) { alert('Could not load PDF: ' + (e.response?.data?.error || e.message)); }
                          }}>
                          📄 PDF
                        </button>
                      )}
                      {!o.signed_at && (o.client_email || o.signer_email) && (
                        <button
                          style={{...S.btn('sm'), background: sentMap[o.id]?'#14532d':'#1e3a5f', color: sentMap[o.id]?'#4ade80':'#60a5fa'}}
                          onClick={() => sendReminder(o)}
                          disabled={sending===o.id}>
                          {sentMap[o.id] ? '✓ Sent!' : sending===o.id ? '...' : '📧 Remind'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded detail row */}
                {expanded === o.id && (
                  <tr style={S.expand}>
                    <td colSpan={7} style={{padding:0, borderBottom:'1px solid #2e3150'}}>
                      <div style={S.detail}>
                        {[
                          ['Signer Name',    o.signer_name],
                          ['Signer Email',   o.signer_email],
                          ['Signer Phone',   o.signer_phone],
                          ['DL Number',      o.signer_dl_number],
                          ['Address',        o.signer_address],
                          ['Payment Status', o.payment_status],
                          ['Amount Paid',    o.amount_paid ? fmt(o.amount_paid) : null],
                          ['Fulfillment',    o.fulfillment_type],
                          ['Notes',          o.notes],
                        ].filter(([,v]) => v).map(([label, val]) => (
                          <div key={label} style={S.dItem}>
                            <div style={S.dLbl}>{label}</div>
                            <div style={S.dVal}>{val}</div>
                          </div>
                        ))}
                        {o.signed_at && (
                          <div style={{...S.dItem, border:'1px solid #14532d'}}>
                            <div style={S.dLbl}>Signed At</div>
                            <div style={{...S.dVal, color:'#4ade80'}}>{fmtDateTime(o.signed_at)}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
